/**
 * Referral attribution helpers.
 *
 * Two phases:
 *
 *   1. captureReferralCode() runs on EVERY initial page load (signed-out
 *      or not). If the URL has ?ref=CODE, we stash the code in localStorage
 *      under STORAGE_KEY. That survives the Clerk redirect dance during
 *      signup. We also drop the param from the URL so the bare share link
 *      doesn't stick around in the browser bar after capture.
 *
 *   2. attributePendingReferral(apiFetch) is called once the user is
 *      AUTHENTICATED (we know who they are). It looks up the stashed code,
 *      POSTs it to /api/affiliate/attribute, and on success clears the
 *      localStorage entry. Idempotent on the backend — re-running for the
 *      same user is a no-op once attributed.
 *
 * Failure modes are silent: invalid codes, network errors, and "already
 * attributed" all just remove the stale code from localStorage so we don't
 * pester users on every page load. The credit grant either happened (and
 * the backend will reflect it) or it didn't — there's no recovery flow.
 */

const STORAGE_KEY = 'dca_referral_code'
// Codes are short uppercase, generated in db/queries/affiliates.ts.
// Keep the validation here loose — the backend is the source of truth.
const VALID_CODE_REGEX = /^[A-Z0-9-]{4,16}$/

/**
 * Pull `?ref=CODE` from the current URL (if present and shaped right),
 * stash it in localStorage, and strip the param from the address bar so
 * the user sees a clean URL. Safe to call multiple times — silently
 * skips when no ref param or invalid shape.
 */
export function captureReferralCode(): void {
  if (typeof window === 'undefined') return
  try {
    const url = new URL(window.location.href)
    const raw = url.searchParams.get('ref')
    if (!raw) return
    const code = raw.trim().toUpperCase()
    if (!VALID_CODE_REGEX.test(code)) return
    window.localStorage.setItem(STORAGE_KEY, code)
    url.searchParams.delete('ref')
    // history.replaceState avoids a navigation that would unmount the
    // currently-rendering route. The capture is best-effort UI polish.
    window.history.replaceState({}, '', url.toString())
  } catch {
    // localStorage disabled / SSR / weird sandbox — silently skip.
  }
}

/**
 * Returns the stashed referral code if one was captured this session, else
 * null. Caller uses this to know whether to attempt attribution after
 * the user authenticates.
 */
export function getStashedReferralCode(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const v = window.localStorage.getItem(STORAGE_KEY)
    return v && VALID_CODE_REGEX.test(v) ? v : null
  } catch {
    return null
  }
}

/**
 * Clear the stashed code. Called after attribution attempts (success OR
 * a "not valid / already attributed" rejection) so we don't loop.
 */
export function clearStashedReferralCode(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

/**
 * One-shot attribution attempt. Caller passes the dashboard's authenticated
 * fetch wrapper (so the JWT and X-User-Id headers go along).
 *
 * Returns { granted: true, credits: N } if this call actually granted the
 * signup bonus. Returns { granted: false } if there was no stashed code,
 * the code wasn't valid, or the user was already attributed.
 */
type AttributeFetchResult<T> = {
  readonly success: boolean
  readonly data?: T
  readonly error?: string
}

type AttributeFetcher = <T>(
  path: string,
  opts?: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown; headers?: Record<string, string> },
) => Promise<AttributeFetchResult<T>>

export async function attributePendingReferral(
  apiFetch: AttributeFetcher,
): Promise<{ readonly granted: boolean; readonly credits?: number; readonly message?: string }> {
  const code = getStashedReferralCode()
  if (!code) return { granted: false }
  try {
    const res = await apiFetch<{
      attributed?: boolean
      credits_granted_to_you?: number
      message?: string
    }>('/api/affiliate/attribute', {
      method: 'POST',
      body: { code },
    })
    // Always clear after an attempt — server is the source of truth and
    // a retry would just no-op anyway thanks to the UNIQUE constraint.
    clearStashedReferralCode()
    if (!res?.success || !res.data) return { granted: false }
    const data = res.data
    if (data.attributed) {
      return {
        granted: true,
        credits: data.credits_granted_to_you,
        message: data.message,
      }
    }
    return { granted: false, message: data.message }
  } catch {
    // Don't leave stale codes lying around even on network errors.
    clearStashedReferralCode()
    return { granted: false }
  }
}
